import React, { useState, useEffect, useCallback } from 'react';
import { Search } from 'lucide-react';
import CourseCard from './CourseCard';
import CourseFilters from './CourseFilters';
import TracksFeatureStrip from './TracksFeatureStrip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CourseCatalog() {
  const [courses, setCourses] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchSubjects = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/courses/subjects`);
      const data = await response.json();
      setSubjects(data.subjects || []);
    } catch (error) {
      /* swallow — non-fatal */
    }
  }, []);

  const fetchCourses = useCallback(async (subject = 'all') => {
    setLoading(true);
    try {
      // FREE is a CLIENT-SIDE filter, not a backend subject. Always fetch
      // the full list when FREE is selected, then narrow locally.
      const isFree = subject === 'FREE';
      const url = (subject === 'all' || isFree)
        ? `${API_URL}/api/courses/`
        : `${API_URL}/api/courses/?subject=${encodeURIComponent(subject)}`;
      const response = await fetch(url);
      let data = await response.json();
      if (subject === 'all' || isFree) setAllCourses(data);
      if (isFree) data = data.filter((c) => c.price === 0);
      setCourses(data);
    } catch (error) {
      /* swallow — non-fatal */
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);
      if (!query.trim()) {
        fetchCourses(selectedSubject);
        return;
      }
      const q = query.toLowerCase();
      const filtered = allCourses.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q) ||
          c.subject.toLowerCase().includes(q)
      );
      setCourses(filtered);
    },
    [allCourses, selectedSubject, fetchCourses]
  );

  useEffect(() => {
    fetchSubjects();
    fetchCourses();
  }, [fetchSubjects, fetchCourses]);

  useEffect(() => {
    fetchCourses(selectedSubject);
  }, [selectedSubject, fetchCourses]);

  return (
    <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Explore Our <span className="text-purple-400">Course Catalog</span>
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto text-base md:text-lg mb-8">
            From art to science, history to technology — discover courses designed to elevate human knowledge
          </p>

          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                data-testid="course-search-input"
                placeholder="Search courses by title, subject, or description..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-gray-900 border-2 border-gray-800 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
              />
            </div>
            {searchQuery && (
              <p className="text-sm text-gray-400 mt-2" data-testid="course-search-count">
                Found {courses.length} course{courses.length !== 1 ? 's' : ''} matching "{searchQuery}"
              </p>
            )}
          </div>
        </div>

        <TracksFeatureStrip />

        <CourseFilters
          subjects={subjects}
          selectedSubject={selectedSubject}
          onSelectSubject={setSelectedSubject}
        />

        {loading ? (
          <div className="text-center py-20" data-testid="catalog-loading">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
            <p className="mt-4 text-gray-400">Loading courses...</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="course-grid">
            {courses.map((course) => (
              <CourseCard key={course.course_id} course={course} />
            ))}
          </div>
        )}

        {courses.length === 0 && !loading && (
          <div className="text-center py-20" data-testid="catalog-empty">
            <p className="text-gray-400 text-lg">No courses found for this subject.</p>
          </div>
        )}
      </div>
    </section>
  );
}
